import firebase from 'firebase/compat/app'
import 'firebase/compat/database'

const DEFAULT_CONFIG = {
  apiKey : 'AIzaSyB-b2ZtP2Yzku5iN05TY99Cnxkg4g_U1jg',
  authDomain : 'leverege-builder.firebaseapp.com',
  databaseURL : 'https://leverege-builder.firebaseio.com',
  projectId : 'leverege-builder',
  storageBucket : 'leverege-builder.appspot.com',
  messagingSenderId : '611592839024'
}

/**
 * This is responsible for updating the css representation
 * in firebase
 *
 * Maybe look into queuing the events and only sending every X milliseconds.
 * Rapid updates are only a problem for colors
 **/
export default class FirebaseSync {
  
  constructor( options = { } ) {
    
    const { config, app, root, onChange } = options
    this.app = app || firebase.initializeApp( config || DEFAULT_CONFIG, 'UIActiveTheme' )
    this.rootRef = options.root ? this.app.database().ref( root ) : this.app.database().ref( )

    this.appearanceId = null
    this.appearanceProjectId = null
    this.isWatchingProject = false
    this.onChange = onChange 
    this.stylesheets = { }
    this.component = { }
    this.theme = { }
  }

  start( { appearanceId, appearanceProjectId } ) {

    this.stop()

    this.appearanceId = appearanceId
    this.appearanceProjectId = appearanceProjectId
    this.isWatchingProject = appearanceProjectId != null
    this.active = ( appearanceId || appearanceProjectId ) != null

    if ( this.isWatchingProject ) {
      this.prjRef = this.rootRef.child( `appearanceProject/${this.appearanceProjectId}` )
      this.prjRef.child( 'activeAppearanceId' ).on( 'value', this.onActiveAppearanceIdChange )
    } else if ( this.appearanceId ) {
      this.setActiveAppearanceIdChanged( this.appearanceId )
    }
  }

  stop( ) {
    this.updateAppearanceListeners( 'off' )
    if ( this.isWatchingProject ) {
      this.prjRef.child( 'activeAppearanceId' ).off( 'value', this.onActiveAppearanceIdChange )
    }
    this.active = false
    this.removeStylesheets()
    this.component = { }
    if ( this.onChange ) {
      this.onChange( this )
    }
  }

  onActiveAppearanceIdChange = ( snap ) => {
    this.setActiveAppearanceIdChanged( snap.val() )
  }

  /**
   * Invoked when the active appearance id changes on the watched project
   **/
  setActiveAppearanceIdChanged = ( id ) => {

    this.updateAppearanceListeners( 'off' )

    this.appearanceId = id
    if ( id ) {
      this.appearanceRef = this.rootRef.child( `appearanceCss/${id}` )
      this.appearanceRef.once( 'value' ).then( ( snap ) => {
        const { theme, component = { } } = ( snap.val() || { } )
       
        this.updateTheme( theme, false )
        Object.keys( component ).forEach( key => this.updateComponent( key, component[key] ) )
        if ( this.onChange ) {
          this.onChange( this )
        }
      } )
      this.updateAppearanceListeners( 'on' )
    }
  }

  updateAppearanceListeners( method ) {
    if ( this.appearanceRef != null ) {
      this.appearanceRef.child( 'theme' )[method]( 'child_added', this.onThemeChange )
      this.appearanceRef.child( 'theme' )[method]( 'child_changed', this.onThemeChange )
      this.appearanceRef.child( 'theme' )[method]( 'child_removed', this.onThemeChange )
      this.appearanceRef.child( 'component' )[method]( 'child_changed', this.onComponentChange )
      if ( method === 'off' ) {
        this.appearanceRef = null
      }
    }
  }

  /**
   * Listen to Css Theme changes from firebase
   **/
  onThemeChange = ( snap ) => {
    const t = { ...this.theme }
    t[snap.key] = snap.val()
    this.updateTheme( t )
    if ( this.onChange ) {
      this.onChange( this, false )
    }
  }

  updateTheme( theme, fire = true ) {
    this.theme = theme || { }
    const css = this.themeToCss( this.theme ) 
    this.createStyleSheet( '_theme_', css )
    this.updateScripts( this.theme )
    if ( fire && this.onChange ) {
      this.onChange( this )
    }
  }

  /**
   * Listen to Css component changes from firebase
   **/
  onComponentChange = ( snap ) => {
    this.updateComponent( snap.key, snap.val() )
    if ( this.onChange ) {
      this.onChange( this )
    }
  }

  updateComponent( type, data ) {
    this.component[type] = data || { }
    const css = this.styleToCss( data.style )
    this.createStyleSheet( type, css )
  }


  createStyleSheet( name, cssText ) {

    try {
      if ( this.stylesheets[name] && this.stylesheets[name].parentNode ) {
        this.stylesheets[name].parentNode.removeChild( this.stylesheets[name] )
      }
      const css = document.createElement( 'style' )
      css.type = 'text/css'
      css.setAttribute( 'data-name', name )
      this.stylesheets[name] = css
      if ( css.styleSheet ) { 
        css.styleSheet.cssText = cssText
      } else { 
        css.appendChild( document.createTextNode( cssText ) )
      }

      document.head.appendChild( this.stylesheets[name] )
    } catch ( err ) {
      console.log( 'Error creating stylesheet', err )
    }
  }

  removeStylesheets( ) {

    Object.values( this.stylesheets ).forEach( ( s ) => { 
      try {
        if ( s && s.parentNode ) {
          s.parentNode.removeChild( s )
        }
      } catch ( err ) {
        console.log( err )
      }
    } )
    this.stylesheets = { }
  }

  /** 
   * Invoked on the initial download of the firebase appearance
   **/
  onAppearanceLoaded( appearance ) {
    if ( !this.ready ) {
      console.log( 'hmm, css sync should be ready' )
    }
    this.onLocalThemeChange( appearance.theme, null )

    // this.onLocalComponentChange( appearance.component, null )
  }

  componentData( name, section ) {
    if ( this.cComponent && this.cComponent[name] ) {
      return this.cComponent[name][section] || {}
    }
    return {}
  }

  setVar( name, value ) {
    const root = document.documentElement;
    root.style.setProperty( name, value )
  }

  themeToCss( theme ) {
    if ( theme == null ) {
      return ''
    }
    const lines = []

    if ( theme.imports ) {
      lines.push( `${theme.imports}\n` )
    }
    if ( theme.vars ) {
      const vars = theme.vars
      lines.push( ':root {' )
      Object.keys( theme.vars ).forEach( ( key ) => {
        lines.push( `${key}: ${vars[key]};` )
      } )
      lines.push( '}' )
    }
    return lines.join( '\n' )
  }
 
  /**
   * Turn component.style into cssText
   **/
  styleToCss( style ) {
    const lines = this.extractStyleSheet( style )
    return lines.join( '\n' )
  }

  /**
   * Walk the style model to extract css. Form is
   * [ { selector, value } | { type : 'conditional', selector, rules [ ] }]
   **/
  extractStyleSheet( style = [ ], pre = '', current ) {
    const lines = current || [ ]
    const nPre = `${pre}  `

    style.forEach( ( rule ) => { 
      lines.push( `${pre}${rule.selector} {` )
      if ( rule.type === 'conditional' ) {
        this.extractStyleSheet( rule.rules, nPre, lines )
      } else {
        this.objToCss( rule.value, lines, nPre )
      }
      lines.push( '}' )
    } )
    return lines
  }

  /**
   * Turn each key/value in obj into a css attribute
   **/
  objToCss( obj, lines, pre ) {
    Object.keys( obj ).forEach( ( k ) => {
      lines.push( `${pre}${k}: ${obj[k]};` )
    } )
  }

  updateScripts( theme ) {
    const arr = theme.scripts || []
    const head = document.head
    const s = head.querySelectorAll( '[data-active-theme-script]' )
    const curr = {}
    for ( let n = 0; n < s.length; n++ ) {
      curr[s[n].id] = s[n]
    }
    for ( let n = 0; n < arr.length; n++ ) {
      const fa = arr[n] || {}
      if ( fa.id && curr[fa.id] == null ) {
        const script = document.createElement( 'script' )
        script.crossOrigin = 'anonymous'
        script.id = fa.id
        script.src = fa.src
        script.setAttribute( 'data-active-theme-script', 'true' )
        head.appendChild( script )
      } else {
        delete curr[fa.id]
      }
    }
    Object.values( curr ).forEach( old => old.remove() )
  }
}
